import dayjs, { Dayjs } from 'dayjs';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FundId,
  PricingRangeUpdateValues,
  ToggleOptionWithPeriod,
  DropdownOption,
  ChartConfig,
} from '@types';
import {
  AnalyticsService,
  DuetDatePickerErrorMsg,
  HighchartsThemeService,
  ResponsiveService,
} from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ANALYTICS_DATE_EVENT,
  ANALYTICS_DATE_FORMAT,
  ANALYTICS_DETAILED_DATE_EVENT,
} from '@utils/app.constants';
import {
  getDatePickerErrorMsg,
  setHighChartLangOptions,
} from '@utils/il8n/moment-il8n';
import { SiteConfigService } from '@services/site-config.service';
import { Observable, Subject } from 'rxjs';
import { generateUUID } from '@utils/text/string-utils';
import { Logger } from '@utils/logger';
import {
  DuetDatePickerCloseEvent,
  DuetDatePickerOpenEvent,
} from '@duetds/date-picker/dist/types/components/duet-date-picker/duet-date-picker';
import { takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';

const logger = Logger.getLogger('PricingChartComponent');

@Component({
  selector: 'ft-pricing-chart',
  templateUrl: './pricing-chart.component.html',
  styleUrls: ['./pricing-chart.component.scss'],
})
export class PricingChartComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() fundId: FundId;
  @Input() chartConfig: ChartConfig;
  @Input() minDateStd: string;
  @Input() maxDateStd: string;
  @Input() navAsOfDate: string;
  @Input() isdatePickerAnalyticsEnabled = true; // For Disabling Analytics for Date Selection
  @Input() performanceInceptionDateStd: string;
  @Output()
  toggleChartRangeUpdated = new EventEmitter<PricingRangeUpdateValues>();
  public highcharts;
  public chartOptions: Highcharts.Options = {};
  public dropdownRange: DropdownOption[] = [];
  public selectedDropdownRange: string;
  public selectedDropdownTitleText: string;
  public fromDateStd: string;
  public toDateStd: string;
  public isMobile$: Observable<boolean>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private dropdownId = `pricingChartDropDown${generateUUID()}`;
  private fromDateId = `pricingChartFromDate${generateUUID()}`;
  private toDateId = `pricingChartToDate${generateUUID()}`;
  private backToTopBtn: HTMLCollection;
  public errorMsgObj: DuetDatePickerErrorMsg;

  public get getDropdownId(): string {
    return this.dropdownId;
  }
  public get getFromDateId(): string {
    return this.fromDateId;
  }
  public get getToDateId(): string {
    return this.toDateId;
  }
  constructor(
    private translateService: TranslateService,
    private analytics: AnalyticsService,
    private highchartsTheme: HighchartsThemeService,
    private siteConfig: SiteConfigService,
    private responsiveService: ResponsiveService,
    @Inject(DOCUMENT) private documentRef: Document
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.errorMsgObj = getDatePickerErrorMsg(this.translateService);
  }

  ngOnInit(): void {
    import(/* webpackChunkName: "highcharts-async" */ 'highcharts').then(
      (highchartsModule) => {
        highchartsModule.setOptions({
          lang: setHighChartLangOptions(this.siteConfig.getPdsLanguage()),
        });
        highchartsModule.setOptions(this.highchartsTheme.themeOptions);
        require('highcharts/modules/accessibility')(highchartsModule);
        this.highcharts = highchartsModule;
      }
    );

    this.populate(this.chartConfig);

    this.backToTopBtn = this.documentRef.getElementsByClassName(
      'page-container__back-to-top'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.chartConfig.firstChange) {
      // avoid duplicate trigger of populate method on first load.
      this.populate(changes.chartConfig.currentValue);
    }
  }

  /**
   * Initializes properties on page load.
   * Again, called when 'input' changes.
   */
  populate(chartConfig: ChartConfig): void {
    this.chartOptions = chartConfig.highChartOptions;
    this.selectedDropdownRange = chartConfig.selectedToggleRange;
    this.fromDateStd = chartConfig.customRange.fromDateStd;
    this.toDateStd = chartConfig.customRange.toDateStd;
    this.constructPriceRangeDropdownOptions(chartConfig);
    this.selectedDropdownTitleText = this.getDropdownTitleText(
      this.selectedDropdownRange
    );
  }

  /**
   * Construct price range toggle options with static values.
   * dropdown selected  will be set as per 'pricingSelectedDropdownRange'.
   */
  constructPriceRangeDropdownOptions(chartConfig: ChartConfig): void {
    this.dropdownRange = Array.from(chartConfig.dateRangeEnum.values())
      .filter(
        (dropdownOption: ToggleOptionWithPeriod) =>
          dropdownOption.value !== 'CUSTOM_RANGE' &&
          this.isValidPeriod(dropdownOption, this.performanceInceptionDateStd)
      )
      .map((dropdownOption: ToggleOptionWithPeriod) => ({
        value: dropdownOption.value,
        label: this.translateService.instant(dropdownOption.text),
        link: '#',
      }));
  }

  isValidPeriod(
    dropdownOption: ToggleOptionWithPeriod,
    targetDate: string
  ): boolean {
    switch (dropdownOption.value) {
      case 'ONE_MONTH':
        return true;
      case 'THREE_MONTHS':
        return dayjs(targetDate).isBefore(dayjs().subtract(3, 'month'));
      case 'SIX_MONTHS':
        return dayjs(targetDate).isBefore(dayjs().subtract(6, 'month'));
      case 'ONE_YEAR':
        return dayjs(targetDate).isBefore(dayjs().subtract(1, 'year'));
      case 'THREE_YEARS':
        return dayjs(targetDate).isBefore(dayjs().subtract(3, 'year'));
      case 'FIVE_YEARS':
        return dayjs(targetDate).isBefore(dayjs().subtract(5, 'year'));
      case 'SINCE_INCEPTION':
        return true;
      case 'YTD':
        return true;
      default:
        return false;
    }
  }

  /**
   * Set price range as per toggle value selected.
   * @param selectedDropdownOption toggle value selected
   */
  setDropdownDateRange(selectedDropdownOption: DropdownOption): void {
    this.toggleChartRangeUpdated.next({
      selectedToggleRange: selectedDropdownOption.value,
    });
  }

  getDropdownTitleText(selectedValue?: string) {
    const selectedDropdown = this.dropdownRange.filter(
      (dropdownOption: DropdownOption) => {
        return dropdownOption.value === selectedValue;
      }
    );
    return selectedDropdown?.length > 0 ? selectedDropdown[0].label : '';
  }

  /**
   * Set price range as per calendar custom value selected.
   * @param inputType the input selected i.e., fromDate/toDate
   * @param event onclick event generated
   */
  setCustomDateRange(inputType: string, date: Dayjs): void {
    logger.debug('date:::::::', date);
    if (dayjs.isDayjs(date)) {
      const selectedDate = dayjs(date).format();

      if (inputType === 'fromDate') {
        this.toggleChartRangeUpdated.next({
          selectedToggleRange: 'CUSTOM_RANGE',
          fromDate: selectedDate,
          toDate: this.toDateStd,
        });
      } else {
        this.toggleChartRangeUpdated.next({
          selectedToggleRange: 'CUSTOM_RANGE',
          fromDate: this.fromDateStd,
          toDate: selectedDate,
        });
      }
      if (this.isdatePickerAnalyticsEnabled) {
        this.analytics.trackEvent({
          event: ANALYTICS_DATE_EVENT,
          detailed_event: ANALYTICS_DETAILED_DATE_EVENT,
          action: 'Date Range',
          label: `${dayjs(this.fromDateStd).format(
            ANALYTICS_DATE_FORMAT
          )} - ${dayjs(this.toDateStd).format(ANALYTICS_DATE_FORMAT)}`,
          event_data: {
            date_type: ANALYTICS_DATE_FORMAT,
            date_value: `${dayjs(this.fromDateStd).format(
              ANALYTICS_DATE_FORMAT
            )} - ${dayjs(this.toDateStd).format(ANALYTICS_DATE_FORMAT)}`,
          },
        });
      }
    }
  }

  datePickerOpened($event: DuetDatePickerOpenEvent) {
    // Hide 'Back to Top' button on mobile
    this.isMobile$.pipe(takeUntil(this.unsubscribe$)).subscribe((isMobile) => {
      if (isMobile) {
        (this.backToTopBtn[0] as HTMLElement).style.opacity = '0';
      }
    });
  }

  datePickerClosed($event: DuetDatePickerCloseEvent) {
    // Show 'Back to Top' button on mobile
    this.isMobile$.pipe(takeUntil(this.unsubscribe$)).subscribe((isMobile) => {
      if (isMobile) {
        (this.backToTopBtn[0] as HTMLElement).style.opacity = '1';
      }
    });
  }

  ngAfterViewInit() {
    if (this.chartOptions.plotOptions && this.chartOptions.plotOptions.series) {
      this.chartOptions.plotOptions.series = {
        ...this.chartOptions.plotOptions.series,
        events: {
          legendItemClick: this.onLegendItemClick.bind(this),
        },
      };
    }
  }

  /**
   *  WDE-5190 as per comments  and WDE:5761 Highcharts legend Disable Legend when Only One Legend
   *
   */
  private onLegendItemClick(event) {
    const shouldProcessClick =
      this.chartOptions.series && this.chartOptions.series.length > 1;
    if (!shouldProcessClick) {
      event.preventDefault();
    }
  }
}
